import { Checkbox } from 'antd';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';

import { TAsset, TAssetGroup, TCollection, TProductCategory } from '@components/types';

interface FiltersProps {
  filterByCategory?: boolean;
  categoryId?: null | number;
  handleCategoryChange?: (id: number) => void;
  allCategories?: (TProductCategory | TAssetGroup)[];
  minimumCategories?: (TProductCategory | TAssetGroup)[];
  filterByCollection?: boolean;
  collectionId?: null | number;
  handleCollectionChange?: (id: number) => void;
  collections?: TCollection[];
  handleClearFilters?: () => void;
  isScroll?: boolean;
}

export default function Filters({
  filterByCategory = true,
  categoryId,
  handleCategoryChange,
  allCategories,
  minimumCategories,
  filterByCollection = false,
  collectionId,
  handleCollectionChange,
  collections,
  handleClearFilters,
  isScroll,
}: FiltersProps) {
  const [type, setType] = useState(true);
  const [categories, setCategories] =
    useState<(TProductCategory | TAssetGroup)[]>(minimumCategories);

  function handleNumOfCatgories() {
    if (categories.length > 10) {
      setCategories(minimumCategories);
    } else {
      setCategories(allCategories);
    }
  }

  return (
    <div className="hidden h-full md:flex flex-col col-span-1">
      <div className="relative z-0 after:content-[''] after:w-1 after:absolute after:z-10 after:bottom-[1px] after:-right-[1px] after:bg-white">
        <div className="h-14 px-4 flex justify-between items-center border border-l-none border-t-none border-solid border-neutral-200">
          <span className="text-sm text-neutral-800 font-semibold">Filters</span>
          <button
            type="button"
            onClick={handleClearFilters}
            className="text-sm text-brand font-manrope font-semibold hover:cursor-pointer"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="overflow-y-scroll no-scrollbar max-h-[calc(100vh-56px)] p-4 relative z-0 space-y-4 after:content-[''] after:w-1 after:h-2.5 after:absolute after:z-10 after:top-0 after:-right-[1px] after:bg-white border border-t-none border-b-none border-l-none border-solid border-neutral-200">
        {filterByCategory && (
          <div className="space-y-2.5 pb-4">
            <button
              type="button"
              onClick={() => setType(!type)}
              className="w-full px-0 flex justify-between items-center hover:cursor-pointer"
            >
              <span className="text-sm text-neutral-800 font-manrope font-semibold">
                Type
              </span>
              <FaChevronDown
                className={`w-3 h-3 text-neutral-600 transition-all duration-200 
                ${type ? 'rotate-180' : 'rotate-0'}
              `}
              />
            </button>

            <motion.div
              animate={{ maxHeight: type ? 10000 : 0, overflow: 'hidden' }}
              className="space-y-2 relative z-0 overflow-hidden"
            >
              {categories.length
                ? categories?.map((category) => (
                    <div>
                      {isScroll ? (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleCategoryChange(category.id)}
                          className={`px-4 w-full md:h-10 h-8 flex justify-start focus:text-brand-500 focus:border-brand-500 text-left items-center rounded border border-solid border-neutral-200 cursor-pointer`}
                        >
                          <span className="text-sm w-full font-manrope line-clamp-1">
                            {category.name}
                          </span>
                        </button>
                      ) : (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleCategoryChange(category.id)}
                          className="px-0 flex items-center gap-2 cursor-pointer"
                        >
                          <Checkbox checked={categoryId === category.id} />
                          <span className="text-sm text-start text-neutral-600 font-manrope font-medium">
                            {category.name}
                          </span>
                        </button>
                      )}
                    </div>
                  ))
                : minimumCategories?.map((category) => (
                    <div>
                      {isScroll ? (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleCategoryChange(category.id)}
                          className={`px-4 w-full h-10 flex justify-start focus:text-brand-500 focus:border-brand-500 text-left items-center rounded border border-solid border-neutral-200  cursor-pointer`}
                        >
                          <span className="text-sm w-full line-clamp-1">
                            {category.name}
                          </span>
                        </button>
                      ) : (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleCategoryChange(category.id)}
                          className="px-0 flex items-center gap-2 cursor-pointer"
                        >
                          <Checkbox checked={categoryId === category.id} />
                          <span className="text-sm text-start text-neutral-600 font-manrope font-medium">
                            {category.name}
                          </span>
                        </button>
                      )}
                    </div>
                  ))}

              <button
                type="button"
                onClick={handleNumOfCatgories}
                className="w-fit py-2 px-0 cursor-pointer"
              >
                <span className="text-sm text-brand-500 font-manrope font-semibold">
                    {categories.length > 10 ? 'Show Less' : (allCategories.length < 10 ? '' : 'Show More')}
                </span>
              </button>
            </motion.div>
          </div>
        )}

        {filterByCollection && (
          <div className="space-y-4">
            <h6 className="text-sm text-neutral-800 font-manrope font-bold">
              Collections
            </h6>

            <div className="space-y-2 5">
              {collections?.map((collection) => (
                <button
                  type="button"
                  key={collection?.id}
                  onClick={() => handleCollectionChange(collection?.id)}
                  className={`w-full h-10 px-4 flex items-center transition-all duration-200 rounded-md border border-solid ${
                    collection?.id === collectionId
                      ? 'border-brand-500'
                      : 'border-neutral-200'
                  } bg-neutral-100 cursor-pointer`}
                >
                  <span
                    className={`text-sm ${
                      collection?.id === collectionId
                        ? 'text-brand-500'
                        : 'text-neutral-800'
                    } font-manrope font-semibold capitalize`}
                  >
                    {collection?.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
